import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { storeAuth, clearAuth, getStoredAuth } from '../services/auth.js';
import type { User, AuthTokens } from '../types/index.js';

export function useAuth() {
  const queryClient = useQueryClient();
  const stored = getStoredAuth();
  const [user, setUser] = useState<User | null>(stored?.user ?? null);
  const [isLoading, setIsLoading] = useState(false);

  const signIn = useCallback(async (email: string, password: string, rememberMe = false) => {
    setIsLoading(true);
    try {
      const result = await api.post<{ user: User; tokens: AuthTokens }>('/auth/signin', {
        email, password, rememberMe,
      });
      if (!result.success) throw new Error(result.error);
      storeAuth(result.data.user, result.data.tokens);
      setUser(result.data.user);
      return { success: true as const };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      return { success: false as const, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signUp = useCallback(async (
    email: string, password: string, firstName: string, lastName: string,
  ) => {
    setIsLoading(true);
    try {
      const result = await api.post<{ user: User; tokens: AuthTokens }>('/auth/signup', {
        email, password, firstName, lastName,
      });
      if (!result.success) throw new Error(result.error);
      storeAuth(result.data.user, result.data.tokens);
      setUser(result.data.user);
      return { success: true as const };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign up failed';
      return { success: false as const, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/signout', {});
    } finally {
      clearAuth();
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  return { user, isLoading, isAuthenticated: !!user, signIn, signUp, signOut };
}
